import { store } from "../core/store";
import { socket } from "../core/socket";
import { saveIdentity } from "../core/storage";
import { showToast } from "../core/toast";
import { listPresetNames, savePreset, loadPreset } from "../core/presets";
import { PLAYER_COLORS } from "../../shared/palette";
import { GameState, type CCCSchedule, type GameConfig, type PlayerListOrder } from "../../shared/types";
import { buildNumberField, buildSelectField, buildBooleanSelectField, buildFieldNote, type FieldSpec } from "./formFields";

// The lobby color grid deliberately shows a different order than the hue-sorted
// PLAYER_COLORS array (still used everywhere else - badges, canvas, achievements):
// two 6-color rows, arranged so each row alternates around the color wheel instead
// of running through it in a single sweep. Values are 1-based positions in
// PLAYER_COLORS per the requested layout (row 1: 1,2,5,6,8,10 - row 2: 12,3,4,7,9,11).
const LOBBY_COLOR_LAYOUT: readonly number[] = [1, 2, 5, 6, 8, 10, 12, 3, 4, 7, 9, 11];
// Exported so colorPicker.ts (the in-lobby "click your swatch to recolor"
// picker) shows the same layout as the join form instead of a different order.
export const LOBBY_COLOR_ORDER: readonly string[] = LOBBY_COLOR_LAYOUT.map((n) => PLAYER_COLORS[n - 1]!);

export function mountJoinForm(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "join-form-wrap";

  const title = document.createElement("h2");
  title.textContent = "Join the Lobby";
  wrap.appendChild(title);

  // Only relevant while a game is already running - see updateProgressUI below,
  // which shows/hides both of these reactively as the game's own state changes
  // (e.g. it could start or end while this screen is sitting open).
  const progressBanner = document.createElement("p");
  progressBanner.className = "hint-text join-in-progress-banner hidden";
  wrap.appendChild(progressBanner);

  const spectateAnonBtn = document.createElement("button");
  spectateAnonBtn.type = "button";
  spectateAnonBtn.className = "secondary-button join-spectate-anon-button hidden";
  spectateAnonBtn.textContent = "👁 Watch as anonymous Spectator";
  wrap.appendChild(spectateAnonBtn);

  // Shared between the spectate button above and the form below, so either
  // path's failure shows in the same place.
  const errorEl = document.createElement("div");
  errorEl.className = "form-error hidden";
  wrap.appendChild(errorEl);

  function showError(message: string): void {
    errorEl.textContent = message;
    errorEl.classList.remove("hidden");
  }

  spectateAnonBtn.addEventListener("click", () => {
    errorEl.classList.add("hidden");
    spectateAnonBtn.disabled = true;
    socket.emit("spectate:anonymous", (res) => {
      spectateAnonBtn.disabled = false;
      if (res.ok) {
        store.setState({ isAnonymousSpectator: true });
      } else {
        showError(res.reason ?? "Unable to spectate right now.");
      }
    });
  });

  const form = document.createElement("form");
  form.className = "join-form";

  const nameLabel = document.createElement("label");
  nameLabel.className = "field-label";
  nameLabel.textContent = "Your name";
  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.maxLength = 20;
  nameInput.required = true;
  nameInput.autocomplete = "off";
  nameInput.placeholder = "e.g. Alex";
  form.append(nameLabel, nameInput);

  const colorLabel = document.createElement("div");
  colorLabel.className = "field-label";
  colorLabel.textContent = "Pick a color";
  form.appendChild(colorLabel);

  const colorGrid = document.createElement("div");
  colorGrid.className = "color-grid";
  let selectedColor: string | null = null;
  let lastTakenSignature = "";
  const colorButtons = new Map<string, HTMLButtonElement>();

  function updateSelectionStyling(): void {
    for (const [color, btn] of colorButtons) {
      btn.classList.toggle("selected", color === selectedColor);
    }
  }

  // Rebuilding the whole grid on every store update (which fires very often while a
  // game is running elsewhere) would invalidate button elements out from under an
  // in-progress click. Only rebuild when the actual set of taken colors changes.
  function renderColors(): void {
    const taken = new Set((store.getState().snapshot?.players ?? []).map((p) => p.color));
    const signature = LOBBY_COLOR_ORDER.map((c) => (taken.has(c) ? "1" : "0")).join("");
    if (signature === lastTakenSignature && colorButtons.size > 0) {
      updateSelectionStyling();
      return;
    }
    lastTakenSignature = signature;

    colorGrid.innerHTML = "";
    colorButtons.clear();
    for (const color of LOBBY_COLOR_ORDER) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "color-grid-swatch";
      btn.style.backgroundColor = color;
      const isTaken = taken.has(color);
      btn.disabled = isTaken;
      if (isTaken) btn.classList.add("taken");
      if (color === selectedColor) btn.classList.add("selected");
      btn.addEventListener("click", () => {
        selectedColor = color;
        updateSelectionStyling();
      });
      colorButtons.set(color, btn);
      colorGrid.appendChild(btn);
    }
  }
  // Reflects "a game is already in progress" - the round/turn message and the
  // anonymous-spectate option only make sense in that circumstance; entering a
  // name and submitting the form below works identically either way (it's the
  // same join:request as always, which is what determines isSpectator server-side).
  function updateProgressUI(): void {
    const snapshot = store.getState().snapshot;
    const inProgress = !!snapshot && snapshot.state !== GameState.WAITING_ROOM;
    progressBanner.classList.toggle("hidden", !inProgress);
    spectateAnonBtn.classList.toggle("hidden", !inProgress);
    if (inProgress && snapshot) {
      const turnPart = snapshot.totalTurnsInRound > 0 ? `, Turn ${snapshot.turnInRound}/${snapshot.totalTurnsInRound}` : "";
      progressBanner.textContent =
        `A game is already in progress - Round ${snapshot.round}/${snapshot.totalRounds}${turnPart}. ` +
        "Enter a name below to spectate and automatically join the next game, or keep the name field empty and watch anonymously.";
    }
  }

  renderColors();
  updateProgressUI();
  const unsub = store.subscribe(() => {
    renderColors();
    updateProgressUI();
  });
  form.appendChild(colorGrid);

  const reclaimRow = document.createElement("div");
  reclaimRow.className = "reclaim-row hidden";
  const reclaimBtn = document.createElement("button");
  reclaimBtn.type = "button";
  reclaimBtn.className = "secondary-button";
  reclaimBtn.textContent = "That's me - reclaim this name";
  reclaimRow.appendChild(reclaimBtn);
  form.appendChild(reclaimRow);

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "primary-button";
  submitBtn.textContent = "Join";
  form.appendChild(submitBtn);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    errorEl.classList.add("hidden");
    reclaimRow.classList.add("hidden");
    const name = nameInput.value.trim();
    if (!name || !selectedColor) {
      showError("Enter a name and pick a color.");
      return;
    }
    socket.emit("join:request", { name, color: selectedColor }, (ack) => {
      if (ack.ok && ack.playerId && ack.reconnectToken) {
        saveIdentity(store.getState().lobbyCode!, {
          playerId: ack.playerId,
          reconnectToken: ack.reconnectToken,
          name,
          color: selectedColor!,
        });
        store.setState({ joined: true, myPlayerId: ack.playerId, isSpectator: !!ack.isSpectator });
      } else {
        showError(ack.reason ?? "Unable to join.");
        if (ack.reason === "That name is already in use.") {
          reclaimRow.classList.remove("hidden");
        }
      }
    });
  });

  reclaimBtn.addEventListener("click", () => {
    const name = nameInput.value.trim();
    if (!name) return;
    const existing = (store.getState().snapshot?.players ?? []).find(
      (p) => p.name.toLowerCase() === name.toLowerCase()
    );
    socket.emit("reclaim:request", { name }, (ack) => {
      if (ack.ok && ack.playerId && ack.reconnectToken) {
        saveIdentity(store.getState().lobbyCode!, {
          playerId: ack.playerId,
          reconnectToken: ack.reconnectToken,
          name,
          color: existing?.color ?? "",
        });
        store.setState({ joined: true, myPlayerId: ack.playerId, isSpectator: !!ack.isSpectator });
      } else {
        showError(ack.reason ?? "Unable to reclaim that name.");
      }
    });
  });

  wrap.appendChild(form);
  container.appendChild(wrap);
  nameInput.focus();

  return () => unsub();
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy fallback below
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

function buildLobbyHeader(lobbyCode: string | null, hostName: string | null): HTMLElement {
  const row = document.createElement("div");
  row.className = "lobby-heading-row";

  const heading = document.createElement("h2");
  heading.textContent = hostName ? `${hostName}'s Lobby` : "Lobby";
  row.appendChild(heading);

  if (lobbyCode) {
    const codeLabel = document.createElement("span");
    codeLabel.className = "lobby-code-label";
    codeLabel.textContent = "Lobby code:";
    row.appendChild(codeLabel);

    const codeEl = document.createElement("span");
    codeEl.className = "lobby-code-display blurred";
    codeEl.textContent = lobbyCode;
    codeEl.title = "Click to show/hide the lobby code";
    codeEl.tabIndex = 0;
    codeEl.addEventListener("click", () => codeEl.classList.toggle("blurred"));
    row.appendChild(codeEl);

    const copyCodeBtn = document.createElement("button");
    copyCodeBtn.type = "button";
    copyCodeBtn.className = "lobby-copy-btn";
    copyCodeBtn.title = "Copy lobby code";
    copyCodeBtn.textContent = "📋 Copy Code";
    copyCodeBtn.addEventListener("click", async () => {
      const ok = await copyToClipboard(lobbyCode);
      showToast(ok ? "Copied lobby code to clipboard." : "Could not copy - copy the code manually.", ok ? "success" : "error");
    });
    row.appendChild(copyCodeBtn);

    const copyLinkBtn = document.createElement("button");
    copyLinkBtn.type = "button";
    copyLinkBtn.className = "lobby-copy-btn";
    copyLinkBtn.title = "Copy lobby link";
    copyLinkBtn.textContent = "📋 Copy Link";
    copyLinkBtn.addEventListener("click", async () => {
      // window.location.href already resolves to the real deployed origin - a
      // placeholder is only needed if that were somehow unavailable, which it
      // never is in a browser context.
      const ok = await copyToClipboard(window.location.href);
      showToast(ok ? "Copied lobby URL to clipboard." : "Could not copy - copy the URL manually.", ok ? "success" : "error");
    });
    row.appendChild(copyLinkBtn);
  }

  return row;
}

const PLAYER_LIST_ORDER_OPTIONS: { value: PlayerListOrder; label: string }[] = [
  { value: "turnOrder", label: "Turn Order" },
  { value: "alphabetical", label: "Name" },
  { value: "points", label: "Score" },
];

const CCC_SCHEDULE_OPTIONS: { value: CCCSchedule; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "startOnly", label: "Once at the Start of the Game" },
  { value: "startAndEveryRound", label: "Start of the Game + After Every Round" },
  { value: "startAndEveryOtherRound", label: "Start of the Game + After Every Other Round" },
  { value: "everyRound", label: "After Every Round" },
  { value: "everyOtherRound", label: "After Every Other Round" },
];

const GAMEPLAY_NUMBER_FIELDS: FieldSpec[] = [
  { path: ["roundsPerGame"], label: "Rounds Per Game", min: 1, max: 99 },
  { path: ["maxLobbySize"], label: "Maximum Lobby Size", min: 2, max: 12 },
  { path: ["timers", "drawingSeconds"], label: "Drawing Time (s)", min: 5, max: 999 },
];

const DIFFICULTY_FIELDS: FieldSpec[] = [
  { path: ["difficultyMultipliers", "easy"], label: "Easy", min: 0, max: 999, scale: 100 },
  { path: ["difficultyMultipliers", "moderate"], label: "Moderate", min: 0, max: 999, scale: 100 },
  { path: ["difficultyMultipliers", "difficult"], label: "Difficult", min: 0, max: 999, scale: 100 },
];

const REROLL_NUMBER_FIELDS: FieldSpec[] = [
  { path: ["scoring", "maxRedraws"], label: "Number of Re-rolls", min: 0, max: 100 },
  { path: ["scoring", "freeRedraws"], label: "Number of Free Re-rolls", min: 0, max: 100 },
];

const HINT_NUMBER_FIELDS: FieldSpec[] = [
  { path: ["hints", "intervalSeconds"], label: "Letter Hint Timer (s)", min: 5, max: 120 },
  { path: ["hints", "maxRevealPercent"], label: "Maximum Word Reveal (%)", min: 10, max: 90 },
];

function buildBox(title: string, helpText?: string): HTMLFieldSetElement {
  const box = document.createElement("fieldset");
  box.className = "lobby-settings-box";
  const legend = document.createElement("legend");
  legend.textContent = title;
  if (helpText) legend.appendChild(buildHelpIcon(helpText));
  box.appendChild(legend);
  return box;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/** A small gray "?" icon that shows `text` in a tooltip on hover or keyboard
 * focus. Positioned via JS against the viewport (like the CCC card tooltip) so
 * the surrounding fieldset/scroll area can never clip it. */
function buildHelpIcon(text: string): HTMLElement {
  const wrap = document.createElement("span");
  wrap.className = "help-icon";
  wrap.tabIndex = 0;
  wrap.setAttribute("role", "img");
  wrap.setAttribute("aria-label", text);

  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "15");
  svg.setAttribute("height", "15");
  svg.setAttribute("aria-hidden", "true");
  const circle = document.createElementNS(SVG_NS, "circle");
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "10.5");
  circle.setAttribute("fill", "currentColor");
  const mark = document.createElementNS(SVG_NS, "path");
  mark.setAttribute("d", "M9.2 9.2a2.9 2.9 0 1 1 4.4 2.5c-.9.6-1.6 1.1-1.6 2.3M12 17.6v.1");
  mark.setAttribute("fill", "none");
  mark.setAttribute("stroke", "var(--bg-panel)");
  mark.setAttribute("stroke-width", "2.2");
  mark.setAttribute("stroke-linecap", "round");
  svg.append(circle, mark);

  const tooltip = document.createElement("span");
  tooltip.className = "help-tooltip";
  tooltip.textContent = text;
  wrap.append(svg, tooltip);

  function show(): void {
    tooltip.style.display = "block";
    const iconRect = wrap.getBoundingClientRect();
    const tipRect = tooltip.getBoundingClientRect();
    const margin = 8;
    const fitsBelow = iconRect.bottom + margin + tipRect.height <= window.innerHeight;
    const top = fitsBelow ? iconRect.bottom + margin : iconRect.top - margin - tipRect.height;
    const idealLeft = iconRect.left + iconRect.width / 2 - tipRect.width / 2;
    tooltip.style.top = `${Math.max(margin, top)}px`;
    tooltip.style.left = `${Math.max(margin, Math.min(idealLeft, window.innerWidth - tipRect.width - margin))}px`;
  }
  function hide(): void {
    tooltip.style.display = "none";
  }
  wrap.addEventListener("mouseenter", show);
  wrap.addEventListener("mouseleave", hide);
  wrap.addEventListener("focus", show);
  wrap.addEventListener("blur", hide);
  return wrap;
}

function buildSettingsColumn(boxes: HTMLElement[]): HTMLElement {
  const column = document.createElement("div");
  column.className = "lobby-settings-column";
  column.append(...boxes);
  return column;
}

/** Left column: Gameplay + Chaos Canvas Cards. Right column: Difficulty
 * Multipliers, Re-rolling Words, Letter Hints. Scoring and the word-selection
 * timer live in Expert Settings instead (src/client/ui/settingsPanel.ts) -
 * everything here is what a host is expected to actually tune between games. */
function buildSettingsGrid(isHost: boolean, getDraft: () => GameConfig | null, onCommit: () => void): HTMLElement {
  const grid = document.createElement("div");
  grid.className = "lobby-settings-grid";

  const gameplayBox = buildBox("Gameplay");
  for (const field of GAMEPLAY_NUMBER_FIELDS) gameplayBox.appendChild(buildNumberField(field, isHost, getDraft, onCommit));
  gameplayBox.appendChild(
    buildBooleanSelectField({ path: ["achievementsEnabled"], label: "Bonus Achievements" }, "On", "Off", isHost, getDraft, onCommit)
  );
  gameplayBox.appendChild(
    buildSelectField(
      { path: ["wordListId"], label: "Word List" },
      store.getState().availableWordLists.map((list) => ({ value: list.id, label: list.label })),
      isHost,
      getDraft,
      onCommit
    )
  );
  gameplayBox.appendChild(
    buildBooleanSelectField({ path: ["wordHintEnabled"], label: "Word Hint" }, "On", "Off", isHost, getDraft, onCommit)
  );
  gameplayBox.appendChild(
    buildSelectField(
      { path: ["playerListOrder"], label: "Sort Player List by" },
      PLAYER_LIST_ORDER_OPTIONS,
      isHost,
      getDraft,
      onCommit
    )
  );
  gameplayBox.appendChild(
    buildBooleanSelectField({ path: ["scoresVisible"], label: "Player Score" }, "Visible", "Hidden", isHost, getDraft, onCommit)
  );
  gameplayBox.appendChild(
    buildBooleanSelectField(
      { path: ["allowSpectators"], label: "Spectators" },
      "Allowed",
      "Not Allowed",
      isHost,
      getDraft,
      onCommit
    )
  );
  gameplayBox.appendChild(
    buildBooleanSelectField(
      { path: ["chat", "wordFilter", "enabled"], label: "Swearword Filter" },
      "On",
      "Off",
      isHost,
      getDraft,
      onCommit
    )
  );

  const cccBox = buildBox(
    "Chaos Canvas Cards",
    "Chaos Canvas Cards are optional handicaps an artist can take on while drawing, in exchange for bonus points. " +
      "For example, “Pixelated” shrinks the canvas to a low resolution, making it much harder to draw. " +
      "Open “How To Play” in the top bar for more details."
  );
  cccBox.appendChild(
    buildSelectField({ path: ["ccc", "schedule"], label: "Appearance" }, CCC_SCHEDULE_OPTIONS, isHost, getDraft, onCommit)
  );
  cccBox.appendChild(
    buildNumberField(
      { path: ["ccc", "selectionSeconds"], label: "Selection Time (s)", min: 5, max: 180 },
      isHost,
      getDraft,
      onCommit
    )
  );

  const difficultyBox = buildBox("Difficulty Multipliers");
  for (const field of DIFFICULTY_FIELDS) difficultyBox.appendChild(buildNumberField(field, isHost, getDraft, onCommit));
  difficultyBox.appendChild(buildFieldNote("The baseline is 100%."));

  const rerollBox = buildBox("Re-rolling Words");
  rerollBox.appendChild(
    buildBooleanSelectField(
      { path: ["scoring", "redrawEnabled"], label: "Re-rolling Words" },
      "Enabled",
      "Disabled",
      isHost,
      getDraft,
      onCommit
    )
  );
  for (const field of REROLL_NUMBER_FIELDS) rerollBox.appendChild(buildNumberField(field, isHost, getDraft, onCommit));
  rerollBox.appendChild(buildFieldNote("Values are per round."));

  const hintsBox = buildBox("Letter Hints");
  hintsBox.appendChild(
    buildBooleanSelectField(
      { path: ["hints", "enabled"], label: "Letter Hints" },
      "Enabled",
      "Disabled",
      isHost,
      getDraft,
      onCommit
    )
  );
  for (const field of HINT_NUMBER_FIELDS) hintsBox.appendChild(buildNumberField(field, isHost, getDraft, onCommit));
  hintsBox.appendChild(buildFieldNote("Values are rounded down."));

  const columns = document.createElement("div");
  columns.className = "lobby-settings-columns";
  columns.append(
    buildSettingsColumn([gameplayBox, cccBox]),
    buildSettingsColumn([difficultyBox, rerollBox, hintsBox])
  );
  grid.appendChild(columns);

  return grid;
}

/** Its own independent "lobby"-scoped preset store (see core/presets.ts) -
 * separate from the Expert Settings modal's "expert"-scoped one. Loading a
 * preset here applies immediately (no "Apply Changes" button in the Lobby),
 * consistent with every other field in this grid. */
function buildLobbyPresetsRow(
  getDraft: () => GameConfig | null,
  onPresetLoaded: (config: GameConfig) => void,
  selectedPresetName: string,
  onSelectionChange: (name: string) => void
): HTMLElement {
  const row = document.createElement("div");
  row.className = "lobby-presets-row";

  const nameInput = document.createElement("input");
  nameInput.type = "text";
  nameInput.placeholder = "Preset name";
  row.appendChild(nameInput);

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "secondary-button";
  saveBtn.textContent = "Save Preset";
  row.appendChild(saveBtn);

  const select = document.createElement("select");
  row.appendChild(select);

  // Takes the name to select explicitly rather than reading `select.value`
  // beforehand - this row is rebuilt from scratch on every re-render (see
  // mountWaitingRoom's `render`), so a freshly created <select> has no prior
  // value of its own to preserve; the caller-tracked `selectedPresetName` is
  // what survives across rebuilds.
  function refreshOptions(nameToSelect: string): void {
    select.innerHTML = "";
    for (const name of listPresetNames("lobby")) {
      const option = document.createElement("option");
      option.value = name;
      option.textContent = name;
      select.appendChild(option);
    }
    if (listPresetNames("lobby").includes(nameToSelect)) select.value = nameToSelect;
  }
  refreshOptions(selectedPresetName);

  select.addEventListener("change", () => onSelectionChange(select.value));

  saveBtn.addEventListener("click", () => {
    const name = nameInput.value.trim();
    const draft = getDraft();
    if (!name || !draft) return;
    savePreset("lobby", name, draft);
    showToast(`Preset "${name}" saved.`, "success");
    refreshOptions(name);
    onSelectionChange(name);
  });

  const loadBtn = document.createElement("button");
  loadBtn.type = "button";
  loadBtn.className = "secondary-button";
  loadBtn.textContent = "Load Preset";
  loadBtn.addEventListener("click", () => {
    const name = select.value;
    if (!name) return;
    const loaded = loadPreset("lobby", name);
    if (!loaded) {
      showToast("Failed to load preset.", "error");
      return;
    }
    onSelectionChange(name);
    onPresetLoaded(loaded);
    showToast(`Preset "${name}" loaded.`, "success");
  });
  row.appendChild(loadBtn);

  return row;
}

export function mountWaitingRoom(container: HTMLElement): () => void {
  const wrap = document.createElement("div");
  wrap.className = "waiting-room";

  let draft: GameConfig | null = null;
  let pendingSaveTimer: ReturnType<typeof setTimeout> | null = null;
  const getDraft = () => draft;
  // Survives the preset row being torn down and rebuilt on every re-render -
  // "Default" so a fresh lobby's picker starts on the preset that matches its
  // actual starting config, not whatever sorts first alphabetically.
  let selectedPresetName = "Default";

  // The store notifies on every unrelated change too. Only rebuild this view's
  // interactive controls when something it actually displays has changed, so a
  // click (or an in-progress settings edit) never lands on an already-replaced
  // element.
  let lastSignature = "";

  function activePlayerInfo(state: ReturnType<typeof store.getState>) {
    const activeCount = (state.snapshot?.players ?? []).filter((p) => !p.isSpectator).length;
    const hostPlayer = (state.snapshot?.players ?? []).find((p) => p.isHost) ?? null;
    return { activeCount, hostPlayer };
  }

  function computeSignature(): string {
    const state = store.getState();
    const { activeCount, hostPlayer } = activePlayerInfo(state);
    return JSON.stringify([
      state.isSpectator,
      state.isHost,
      activeCount,
      hostPlayer?.id ?? null,
      hostPlayer?.name ?? null,
      draft,
      state.availableWordLists,
    ]);
  }

  function sendConfigUpdate(config: GameConfig): void {
    socket.emit("host:updateConfig", config, (ok) => {
      if (!ok) showToast("Failed to apply settings.", "error");
    });
  }

  // Wholesale swap-in, unlike sendConfigUpdate's merge-onto-current semantics -
  // a preset saved before some field existed is missing that field entirely, so
  // merging it onto whatever's currently set would silently leave that field
  // untouched instead of resetting it to the preset's own (schema-default)
  // value. Only preset loads need this; every other write here already always
  // sends a complete, gap-free draft, so it'd behave identically either way.
  function sendConfigReplace(config: GameConfig): void {
    socket.emit("host:replaceConfig", config, (ok) => {
      if (!ok) showToast("Failed to apply preset.", "error");
    });
  }

  // Edits auto-apply instead of requiring an explicit "Apply Changes" click -
  // debounced so a burst of edits (or holding a number spinner) coalesces into
  // one `host:updateConfig` roughly a second after the last change.
  function onFieldCommit(): void {
    // A local edit mutates `draft` in place synchronously, but doesn't itself
    // trigger a render - so without this, the *next* render() call (typically
    // this very edit's own auto-save echoing back down as a `config:current`
    // broadcast) would see the signature "change" simply because it's catching
    // up to an edit it doesn't know about yet, and rebuild the whole grid as a
    // result - force-closing any native <select> dropdown the host has open on
    // a different field in the meantime. Recomputing `lastSignature` right now,
    // synchronously with the edit, keeps it truthful so that later render()
    // call finds nothing new to do.
    lastSignature = computeSignature();
    if (pendingSaveTimer) clearTimeout(pendingSaveTimer);
    pendingSaveTimer = setTimeout(() => {
      pendingSaveTimer = null;
      if (draft) sendConfigUpdate(draft);
    }, 1000);
  }

  // A preset load is a single deliberate click, not a mid-typing edit - unlike
  // onFieldCommit, this *should* immediately rebuild the grid (every field's
  // displayed value needs to jump to the preset's), and applies right away
  // rather than waiting out the usual debounce.
  function onPresetLoaded(config: GameConfig): void {
    if (pendingSaveTimer) {
      clearTimeout(pendingSaveTimer);
      pendingSaveTimer = null;
    }
    const cloned: GameConfig = JSON.parse(JSON.stringify(config));
    draft = cloned;
    sendConfigReplace(cloned);
    render();
  }

  const render = (): void => {
    const state = store.getState();
    if (state.config) {
      // Once this client is host, `draft` becomes the sole source of truth for
      // what's rendered - it's never overwritten by an incoming `config:current`
      // broadcast again (including the echo of the host's own auto-save). A
      // comparison-based resync used to live here, but comparing via
      // `JSON.stringify` is key-order-sensitive: the server's config object
      // doesn't necessarily serialize in the same key order as the client's
      // locally-cloned draft, so the (spurious) "different" result forced a
      // rebuild on every single auto-save round trip - destroying any native
      // <select> dropdown the host happened to have open at that moment. A
      // non-host has no editable draft to protect, so it's safe (and desired,
      // for live-updating visibility) to keep resyncing theirs from the server
      // on every broadcast.
      if (!draft || !state.isHost) {
        draft = JSON.parse(JSON.stringify(state.config));
      }
    }
    const signature = computeSignature();
    if (signature === lastSignature) return;
    lastSignature = signature;

    const { activeCount, hostPlayer } = activePlayerInfo(state);

    wrap.innerHTML = "";

    const topRow = document.createElement("div");
    topRow.className = "lobby-top-row";

    const topLeft = document.createElement("div");
    topLeft.className = "lobby-top-left";
    topLeft.appendChild(buildLobbyHeader(state.lobbyCode, hostPlayer?.name ?? null));

    const summary = document.createElement("p");
    summary.className = "hint-text";
    summary.textContent = `${activeCount} player${activeCount === 1 ? "" : "s"} ready. Minimum 2 to start.`;
    topLeft.appendChild(summary);
    topRow.appendChild(topLeft);

    if (state.isHost) {
      const startBtn = document.createElement("button");
      startBtn.type = "button";
      startBtn.className = "primary-button lobby-start-btn";
      startBtn.textContent = "Start Game";
      startBtn.disabled = activeCount < 2;
      startBtn.addEventListener("click", () => {
        // A just-edited field's auto-apply is debounced (~1s) - starting
        // immediately after an edit could otherwise race it, launching the
        // game against the config as it was *before* that edit. Flushing here
        // (and relying on Socket.IO's in-order delivery over this one
        // connection) guarantees the server applies the latest draft before
        // it ever sees host:startGame.
        if (pendingSaveTimer) {
          clearTimeout(pendingSaveTimer);
          pendingSaveTimer = null;
        }
        if (draft) sendConfigUpdate(draft);
        socket.emit("host:startGame");
      });
      topRow.appendChild(startBtn);
    }
    wrap.appendChild(topRow);

    if (state.isSpectator) {
      const note = document.createElement("p");
      note.className = "hint-text";
      note.textContent = "You joined mid-game and are spectating. You'll automatically join the next game.";
      wrap.appendChild(note);
    }

    if (!state.isHost) {
      const waitHint = document.createElement("p");
      waitHint.className = "hint-text";
      waitHint.textContent = "Waiting for the host to start the game.";
      wrap.appendChild(waitHint);
    }

    if (draft) {
      wrap.appendChild(buildSettingsGrid(state.isHost, getDraft, onFieldCommit));
      if (state.isHost) {
        wrap.appendChild(
          buildLobbyPresetsRow(getDraft, onPresetLoaded, selectedPresetName, (name) => {
            selectedPresetName = name;
          })
        );
      }
    }
  };

  render();
  const unsub = store.subscribe(render);
  container.appendChild(wrap);
  return () => {
    if (pendingSaveTimer) clearTimeout(pendingSaveTimer);
    unsub();
  };
}
